import type { CompanyLinkedinSelection } from "../components/CompanyLinkedinCombobox";

/** The subset of the logged-in user's company the prefill chip is built from. */
export interface CompanyPrefill {
	name?: string | null;
	website?: string | null;
	linkedinUrl?: string | null;
	fromOrganization?: {
		image?: string | null;
		title?: string | null;
	} | null;
}

/** Company slug of a LinkedIn page URL ("…/company/altengermany" → "altengermany"). */
const linkedinCompanySlug = (url: string): string | undefined =>
	/linkedin\.com\/company\/([^/?#]+)/i.exec(url)?.[1];

/** Bare domain of a stored website URL ("https://alten-germany.de/" → "alten-germany.de"). */
const websiteDomain = (website?: string | null): string | undefined =>
	website
		?.trim()
		.replace(/^https?:\/\//i, "")
		.replace(/\/+$/, "") || undefined;

/**
 * Turns the company already stored on the account into a combobox selection, so
 * a pre-filled LinkedIn page shows as a confirmed chip instead of sending the
 * user to the manual-URL fallback while the typeahead sits empty (PREC-3719).
 * Returns `null` when there is no LinkedIn URL to confirm.
 */
export const companyPrefillSelection = (
	company?: CompanyPrefill | null
): CompanyLinkedinSelection | null => {
	const linkedinUrl = company?.linkedinUrl?.trim();

	if (!linkedinUrl) {
		return null;
	}

	const name =
		company?.name?.trim() ||
		company?.fromOrganization?.title?.trim() ||
		linkedinCompanySlug(linkedinUrl) ||
		linkedinUrl;

	return {
		name,
		domain: websiteDomain(company?.website),
		logoUrl: company?.fromOrganization?.image ?? undefined,
		linkedinUrl,
	};
};
